import React, { Fragment, useMemo } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import SmartForm from "src/container/smart-form.component";
import { getMedvolAdminUsers } from "src/services/employee-api";
import { uploadFileToS3 } from "src/services/feedback-api";
import { Button } from "src/components";
import { useToast } from "src/hooks/useToast";
import { useForm } from "react-hook-form";

interface AddFeedbackFormProps {
  onFormSubmit: (payload: any) => Promise<boolean>;
  isLoading?: boolean;
  defaultValues?: any;
  setShowAddEditForm?: (show: boolean) => void;
}

const TYPE_OPTIONS = [
  { name: "Employees", id: "employee" },
  { name: "Products", id: "product" },
  { name: "Counters", id: "counter" },
  { name: "Stockists", id: "stockist" },
  { name: "Geographic", id: "geographic" },
  { name: "CRA", id: "cra" },
  { name: "PRA", id: "pra" },
  { name: "Orders", id: "order" },
  { name: "Invoices", id: "invoice" },
  { name: "Purchase Order", id: "purchase_order" },
  { name: "Reconciliation", id: "reconciliation" },
  { name: "Reports", id: "report" },
  { name: "Ticket", id: "ticket" },
  { name: "Whatsapp", id: "whatsapp" }
];

const AddFeedbackForm: React.FC<AddFeedbackFormProps> = ({ onFormSubmit, isLoading, defaultValues, setShowAddEditForm }) => {
  const { toast } = useToast();

  const fetchEmployees = async () => {
    const data = getMedvolAdminUsers();
    return data;
  };

  const uploadAttachment = async (file: File): Promise<string | null> => {
    try {
      const uploadFiledata = new FormData();
      uploadFiledata.append("file", file);

      const res = await uploadFileToS3(uploadFiledata);

      if (res?.status !== 200) {
        throw new Error("Failed to upload attachment");
      }

      return res.data?.attachment_path || null;
    } catch (error) {
      console.error("Error uploading attachment:", error);
      toast({
        variant: "destructive",
        description: "Failed to upload attachment",
      });
      return null;
    }
  };

  const formDefaultValues = {
    employee: null,
    type: null,
    reference_number: "",
    description: "",
    thumbs: null,
    attachment: null,
  };

  const schema = yup.object().shape({
    employee: yup.mixed().nullable().required("Employee is required"),
    type: yup.mixed().nullable().required("Type is required"),
    reference_number: yup.string().required("Reference Number is required"),
    description: yup.string().nullable(),
    thumbs: yup
      .string()
      .nullable()
      .oneOf(["up", "down"], "Please select thumbs up or thumbs down")
      .required("Feedback is required"),
    attachment: yup.mixed().nullable(),
  });

  const smartFormInstance = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema) as any,
    defaultValues: {
      ...formDefaultValues,
      ...defaultValues,
    },
  });

  const createFeedbackForm = useMemo(() => {
    const fields: any[] = [
      {
        id: "1",
        label: "Employee Name",
        type: "autocomplete",
        keyofForm: "employee",
        asyncListFunction: fetchEmployees,
        isAsyncQuery: false,
        suggestionList: [],
        displayKey: "name",
        additionalDisplayKey: "user_role.role",
        uniqueKey: "ucode",
      },
      {
        id: "2",
        label: "Type",
        type: "autocomplete",
        suggestionList: TYPE_OPTIONS,
        displayKey: "name",
        uniqueKey: "id",
        keyofForm: "type",
        disabled: defaultValues?.type ? true : false,
      },
      {
        id: "3",
        label: "Reference Number",
        type: "input",
        keyofForm: "reference_number",
        placeholder: "Enter reference number",
        disabled: defaultValues?.reference_number ? true : false,
      },
      {
        id: "4",
        label: "Description",
        type: "textarea",
        keyofForm: "description",
        size: "full",
      },
      {
        id: "5",
        label: "Attachment",
        type: "attachment",
        keyofForm: "attachment",
      },
      {
        id: "6",
        label: "Feedback",
        type: "thumbs",
        keyofForm: "thumbs",
      },
    ];

    return fields;
  }, [defaultValues]);

  const handleSubmit = async (data: any) => {
    try {
      let attachmentPath: string | null = null;

      if (data?.type?.id === "whatsapp" && data?.attachment) {
        attachmentPath = await uploadAttachment(data.attachment);
        if (!attachmentPath) {
          throw new Error("Failed to upload attachment");
        }
      }

      const payload: any = {
        employee_ucode: data.employee.ucode,
        employee_name: data.employee.name,
        employee_cognito_id: data.employee.cognito_id,
        employee_role: data.employee.user_role.role,
        feedback_type: data.type.name,
        reference_number: data.reference_number,
        description: data.description,
        feedback_rating: data.thumbs === "up" ? "UP" : "DOWN",
      };

      if (attachmentPath) {
        payload.attachment_file_path = attachmentPath;
      }

      const isSuccess = await onFormSubmit(payload);

      if (isSuccess) {
        smartFormInstance.reset(structuredClone(formDefaultValues));

        if (setShowAddEditForm) {
          setShowAddEditForm(false);
        }
      }
    } catch (err: any) {
      toast({
        variant: "destructive",
        description: err?.message || "Failed to submit feedback",
      });
    }
  };

  return <SmartForm formInstance={smartFormInstance} isControlled={false} onFormSubmit={handleSubmit}>
    <div className="p-6 max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
      {createFeedbackForm.map(({ label, type, id, placeholder, keyofForm, suggestionList, displayKey, additionalDisplayKey, uniqueKey, asyncListFunction, disabled }: any) => {
        switch (type.toString().toLocaleLowerCase().trim()) {
          case "autocomplete":
            return (
              <Fragment key={id}>
                <div className="w-full">
                  <SmartForm.Autocomplete
                    fieldName={keyofForm}
                    label={label}
                    disabled={disabled}
                    suggestionList={suggestionList || []}
                    displayKey={displayKey}
                    additionDisplayKey={additionalDisplayKey}
                    uniqueKey={uniqueKey}
                    isControlled={false}
                    asyncListFunction={asyncListFunction}
                    placeholder={placeholder || ""}
                    className="w-full"
                  />
                </div>
              </Fragment>
            );

          case "textarea":
            return (
              <Fragment key={id}>
                <div>
                  <SmartForm.TextArea type="text" label={label} placeholder={placeholder || ""} fieldName={keyofForm} className="w-full mr-auto" inputClassName="w-full" />
                </div>
              </Fragment>
            );

          case "attachment":
            const selectedType = smartFormInstance.watch("type");
            if (selectedType?.id !== "whatsapp") {
              return null;
            }
            return (
              <SmartForm.Attachment
                key={id}
                fieldName={keyofForm}
                label={label}
                className="w-full"
              />
            );

          case "thumbs":
            return (
              <div key={id} className="w-full">
                <SmartForm.Thumbs
                  fieldName={keyofForm}
                  label={label}
                  className="w-full"
                />
              </div>
            );

          case "checkbox":
            return <SmartForm.Checkbox key={id} fieldName={keyofForm} label={label} className="w-full" />;

          default:
            return (
              <SmartForm.Input key={id} type={type} placeholder={placeholder || ""} label={label} fieldName={keyofForm} disabled={disabled} className="w-full mr-auto" inputClassName="w-full" />
            );
        }
      })}

      <div className="flex justify-end w-full col-span-1 sm:col-span-2 pt-6 border-t mt-4">
        <Button
          isLoading={isLoading}
          loaderSize="small"
          type="submit"
          className="bg-[#7F56D9] text-white px-6"
        >
          Submit
        </Button>
      </div>
    </div>
  </SmartForm>;
};

export default AddFeedbackForm;
